import { useCallback, useEffect, useRef } from 'react';
import { MathUtils, Vector2, Vector3 } from 'three';
import { useFrame, useThree, type RootState } from '@react-three/fiber';
import { CAMERA_SETTINGS } from 'modules/editor/constants';
import { useEditorStore } from 'modules/editor/store';
import { dispatcher, EditorEventType } from 'modules/editor/events';

type Lerp<T> = { from: T; to: T };
interface Pan {
  duration: Lerp<number>;
  zoom: Lerp<number>;
  position: Lerp<Vector3>;
}

const withDpr = (value: number) => {
  return value / Math.min(window.devicePixelRatio, 1);
};

const clampZoom = (zoom: number) => {
  const { minZoom, maxZoom } = CAMERA_SETTINGS;
  return MathUtils.clamp(withDpr(zoom), minZoom, maxZoom);
};

const scaleFactorFromZoom = (zoom: number) => CAMERA_SETTINGS.defaultZoom / zoom;

export const changeZoom = (direction: 'in' | 'out', zoom: number, factor?: number) => {
  if (factor !== undefined) {
    // Mouse wheel
    return clampZoom(zoom + withDpr(factor));
  }

  // button click or API call
  const sensitivity = direction === 'in' ? 1 / CAMERA_SETTINGS.sensitivity : CAMERA_SETTINGS.sensitivity;
  const step = sensitivity * zoom - zoom;

  return clampZoom(zoom + step);
};

type MousePosition = [number, number];
type MouseState = {
  pan: Pan | null;
  button: MouseEvent['button'] | null;
  mouse: {
    initial: MousePosition;
    current: MousePosition;
  };
};

type PinchState = {
  pointers: number[];
  dollyStart: Vector2;
  dollyEnd: Vector2;
  dollyDelta: Vector2;
  pointerPositions: Record<string, Vector2>;
};

const getInitialPinchState = (): PinchState => ({
  pointers: [],
  dollyStart: new Vector2(),
  dollyEnd: new Vector2(),
  dollyDelta: new Vector2(),
  pointerPositions: {},
});

const getInitialState = (): MouseState => ({
  pan: null,
  button: null,
  mouse: {
    initial: [0, 0],
    current: [0, 0],
  },
});

const viewboxPosition = new Vector3();

const getPan = ({
  camera,
  clock,
  position,
  zoom,
}: Pick<RootState, 'camera' | 'clock'> & { position: Vector3; zoom: number }) => {
  const startedTime = clock.getElapsedTime();
  return {
    duration: { from: startedTime, to: startedTime + CAMERA_SETTINGS.panDuration },
    position: { from: camera.position.clone(), to: position },
    zoom: { from: camera.zoom, to: zoom },
  };
};

export const useCamera = () => {
  const {
    gl: { domElement },
    camera,
    pointer,
    size,
    clock,
  } = useThree();

  const fiberRef = useRef({ domElement, camera, pointer, size, clock });
  fiberRef.current = { domElement, camera, pointer, size, clock };

  const stateRef = useRef(getInitialState());
  const gestureRef = useRef(getInitialPinchState());

  const isTouch = useCallback((event: PointerEvent) => event.pointerType === 'touch', []);
  const isPinchGesture = useCallback(() => gestureRef.current.pointers.length > 1, []);
  const getSecondPointerPosition = useCallback((event: PointerEvent) => {
    const pointers = gestureRef.current.pointers;
    const pointerId = pointers.length > 1 && event.pointerId === pointers[0] ? pointers[1] : pointers[0];

    return gestureRef.current.pointerPositions[pointerId];
  }, []);
  const setDolly = useCallback(
    (event: PointerEvent, dolly: Vector2) => {
      const position = getSecondPointerPosition(event);
      const dx = event.pageX - position.x;
      const dy = event.pageY - position.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      dolly.set(0, distance);
    },
    [getSecondPointerPosition]
  );
  const trackPointer = useCallback((event: PointerEvent) => {
    const pointerPositions = gestureRef.current.pointerPositions;
    let position = pointerPositions[event.pointerId];

    if (position === undefined) {
      position = new Vector2();
      pointerPositions[event.pointerId] = position;
    }

    position.set(event.pageX, event.pageY);
  }, []);

  // Used for getting mouse position relative to pan/zoom
  const getMousePosition = useCallback((event: PointerEvent): MousePosition => {
    const { camera } = fiberRef.current;

    const x = camera.position.x + event.x / camera.zoom;
    const y = camera.position.y - event.y / camera.zoom;

    return [x, y];
  }, []);

  const onZoom = useCallback((deltaY: number, factor = 0.01) => {
    const { camera, pointer } = fiberRef.current;

    const zoomBefore = camera.zoom;
    const zoomAfter = changeZoom(deltaY < 0 ? 'in' : 'out', zoomBefore, deltaY * factor * -1);
    if (zoomBefore === zoomAfter) {
      // we can end up here when zoom boundaries are reached
      return;
    }

    const farNearSlope = (camera.near + camera.far) / (camera.near - camera.far);
    viewboxPosition.set(pointer.x, pointer.y, farNearSlope).unproject(camera);

    camera.zoom = zoomAfter;
    useEditorStore.setState({ scaleFactor: scaleFactorFromZoom(camera.zoom) });

    camera.position.lerp(viewboxPosition, 1 - zoomBefore / zoomAfter);
    camera.updateProjectionMatrix();

    dispatcher.zoomChanged();
  }, []);

  // Event callbacks
  const onWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      onZoom(e.deltaY);
    },
    [onZoom]
  );

  const pan = useCallback((event: PointerEvent) => {
    event.preventDefault();

    const { camera } = fiberRef.current;

    const [x1, y1] = stateRef.current.mouse.initial;
    const [x2, y2] = stateRef.current.mouse.current;
    const [x, y] = camera.position.toArray();

    camera.position.setX(x - (x2 - x1));
    camera.position.setY(y - (y2 - y1));
    camera.updateProjectionMatrix();
    dispatcher.panChanged();
  }, []);

  const onPointerDown = useCallback(
    (event: PointerEvent) => {
      if (useEditorStore.getState().hoverDeviceId) {
        return;
      }

      if (isTouch(event)) {
        gestureRef.current.pointers.push(event.pointerId);
        trackPointer(event);
        setDolly(event, gestureRef.current.dollyStart);
      }

      fiberRef.current.domElement.style.cursor = 'move';

      const [x, y] = getMousePosition(event);
      stateRef.current.mouse.initial = [x, y];
      stateRef.current.mouse.current = [x, y];

      stateRef.current.button = event.button;

      if (event.button === 0 && !isPinchGesture()) {
        useEditorStore.setState({ state: 'panning' });
      }
    },
    [isTouch, isPinchGesture, getMousePosition, trackPointer, setDolly]
  );

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      if (useEditorStore.getState().hoverDeviceId) {
        return;
      }

      if (isPinchGesture()) {
        trackPointer(event);
        setDolly(event, gestureRef.current.dollyEnd);
        gestureRef.current.dollyDelta.set(
          0,
          Math.pow(gestureRef.current.dollyEnd.y / gestureRef.current.dollyStart.y, 1)
        );

        onZoom(1 - gestureRef.current.dollyDelta.y, 15);
        gestureRef.current.dollyStart.copy(gestureRef.current.dollyEnd);

        return;
      }

      stateRef.current.mouse.current = getMousePosition(event);

      // mouse drag with left button
      if (stateRef.current.button === 0) {
        pan(event);
      }
    },
    [getMousePosition, isPinchGesture, onZoom, setDolly, trackPointer, pan]
  );

  const onPointerUp = useCallback(() => {
    stateRef.current = getInitialState();
    fiberRef.current.domElement.style.cursor = 'auto';
    gestureRef.current = getInitialPinchState();

    if (useEditorStore.getState().state === 'panning') {
      useEditorStore.setState({ state: 'idle' });
    }
  }, []);

  const onPointerLeave = useCallback(() => {
    stateRef.current = getInitialState();
  }, []);

  useEffect(() => {
    const { domElement } = fiberRef.current;

    domElement.addEventListener('wheel', onWheel, { passive: false });
    domElement.addEventListener('pointerdown', onPointerDown);
    domElement.addEventListener('pointermove', onPointerMove);
    domElement.addEventListener('pointerup', onPointerUp);
    domElement.addEventListener('pointerleave', onPointerLeave);

    return () => {
      stateRef.current = getInitialState();
      domElement.removeEventListener('wheel', onWheel);
      domElement.removeEventListener('pointerdown', onPointerDown);
      domElement.removeEventListener('pointermove', onPointerMove);
      domElement.removeEventListener('pointerup', onPointerUp);
      domElement.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [onWheel, onPointerDown, onPointerMove, onPointerUp, onPointerLeave]);

  const panTo = useCallback(() => {
    const pan = stateRef.current.pan;
    if (!pan) {
      return;
    }
    const { camera, clock } = fiberRef.current;
    const { zoom, position } = pan;

    // Stop if destination is reached
    const shouldPan = camera.position.distanceTo(position.to) > 0.01;
    const shouldZoom = Math.abs(camera.zoom - zoom.to) > 0.01;
    if (!shouldPan && !shouldZoom) {
      stateRef.current.pan = null;
      return;
    }

    const duration = pan.duration.to - pan.duration.from;
    const alpha = !duration ? 1 : MathUtils.clamp((clock.elapsedTime - pan.duration.from) / duration, 0, 1);
    camera.zoom = MathUtils.lerp(zoom.from, zoom.to, alpha);
    useEditorStore.setState({ scaleFactor: scaleFactorFromZoom(camera.zoom) });

    camera.position.copy(position.from.clone().lerp(position.to, alpha));
    camera.updateProjectionMatrix();

    dispatcher.zoomChanged();
    dispatcher.panChanged();

    if (alpha === 1) {
      stateRef.current.pan = null;
    }
  }, []);

  // zoomIn | zoomOut | fit scene handlers
  useEffect(() => {
    const { camera, clock } = fiberRef.current;

    const onZoom = (direction: 'in' | 'out') => () => {
      stateRef.current.pan = getPan({
        camera,
        clock,
        position: camera.position,
        zoom: changeZoom(direction, camera.zoom),
      });
    };

    const onZoomIn = onZoom('in');
    const onZoomOut = onZoom('out');

    const onFitScene = () => {
      let minX = Number.POSITIVE_INFINITY;
      let minY = Number.POSITIVE_INFINITY;
      let maxX = Number.NEGATIVE_INFINITY;
      let maxY = Number.NEGATIVE_INFINITY;

      Object.values(useEditorStore.getState().devices)
        .map((device) => device.position)
        .forEach(([x, y]) => {
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        });

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      const xDiff = maxX - minX;
      const yDiff = maxY - minY;

      const padding = 80;
      const bottomPanelPadding = 300;

      const zoomForX = (size.width - padding) / xDiff;
      const zoomForY = (size.height - padding - bottomPanelPadding) / yDiff;

      const zoom = Math.max(Math.min(zoomForX, zoomForY, CAMERA_SETTINGS.maxZoom), CAMERA_SETTINGS.minZoom);

      stateRef.current.pan = getPan({
        camera,
        clock,
        position: new Vector3(
          centerX,
          centerY - (bottomPanelPadding - (padding + bottomPanelPadding) / 2) / zoom,
          camera.position.z
        ),
        zoom,
      });
    };

    dispatcher.addEventListener(EditorEventType.ZOOM_IN, onZoomIn);
    dispatcher.addEventListener(EditorEventType.ZOOM_OUT, onZoomOut);
    dispatcher.addEventListener(EditorEventType.FIT_SCENE, onFitScene);

    return () => {
      dispatcher.removeEventListener(EditorEventType.ZOOM_IN, onZoomIn);
      dispatcher.removeEventListener(EditorEventType.ZOOM_OUT, onZoomOut);
      dispatcher.removeEventListener(EditorEventType.FIT_SCENE, onFitScene);
    };
  }, [camera.position.z, size.width, size.height]);

  // "animate" on each frame
  useFrame(panTo);
};
